import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dumbbell, Plus, Edit, Trash2, X, Target, Settings } from "lucide-react";
import { toast } from "sonner";
import { API } from "./SharedComponents";

const EquipmentManager = () => {
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: "", weight: "", keywords: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchEquipment();
  }, []);

  const fetchEquipment = async () => {
    try {
      const response = await fetch(`${API}/coach/bar-equipment`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setEquipment(data.equipment || []);
      }
    } catch (error) {
      console.error("Failed to fetch equipment:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const keywordsArray = formData.keywords
      .split(",")
      .map(k => k.trim().toLowerCase())
      .filter(k => k);

    const payload = {
      name: formData.name,
      weight: parseFloat(formData.weight) || 0,
      keywords: keywordsArray.length > 0 ? keywordsArray : undefined
    };

    try {
      const url = editingItem 
        ? `${API}/coach/bar-equipment/${editingItem.equipment_id}`
        : `${API}/coach/bar-equipment`;
      
      const response = await fetch(url, {
        method: editingItem ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(editingItem ? "Equipment updated!" : "Equipment created!");
        setShowForm(false);
        setEditingItem(null);
        setFormData({ name: "", weight: "", keywords: "" });
        fetchEquipment();
      } else {
        toast.error("Failed to save equipment");
      }
    } catch (error) {
      console.error("Error saving equipment:", error);
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (equipmentId) => {
    if (!window.confirm("Delete this equipment?")) return;

    try {
      const response = await fetch(`${API}/coach/bar-equipment/${equipmentId}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Equipment deleted!");
        fetchEquipment();
      } else {
        toast.error("Failed to delete equipment");
      }
    } catch (error) {
      console.error("Error deleting equipment:", error);
      toast.error("Something went wrong");
    }
  };

  const startEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      weight: item.weight.toString(),
      keywords: (item.keywords || []).join(", ")
    });
    setShowForm(true);
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setFormData({ name: "", weight: "", keywords: "" });
    setShowForm(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold">Equipment Management</h2>
          <p className="text-gray-400 text-sm mt-1">
            Define bar weights for automatic plate calculations in athlete workouts
          </p>
        </div>
        <button
          onClick={handleAddNew}
          className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-600"
          data-testid="add-equipment-btn"
        >
          <Plus className="w-4 h-4" />
          Add Equipment
        </button>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Target className="w-5 h-5 text-amber-400 mt-0.5" />
          <div>
            <h4 className="font-semibold text-amber-400">How Equipment Works</h4>
            <p className="text-sm text-gray-400 mt-1">
              When athletes view workouts, the system matches exercise names to your equipment keywords. 
              For example, if an exercise contains "squat", it might match "Straight Bar" or "Safety Squat Bar". 
              The matched bar weight is used to calculate "Plates per Side" = (Total Weight - Bar Weight) / 2
            </p>
          </div>
        </div>
      </div>

      {equipment.length === 0 ? (
        <div className="text-center py-12 bg-[#141B2D] rounded-xl border border-[#1E293B]">
          <Settings className="w-12 h-12 mx-auto text-gray-500 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No Equipment Yet</h3>
          <p className="text-gray-400 mb-4">Add your first equipment item to enable plate calculations</p>
          <button
            onClick={handleAddNew}
            className="bg-primary text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-600"
          >
            Add Equipment
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {equipment.map((item) => (
            <div
              key={item.equipment_id}
              className="bg-[#141B2D] border border-[#1E293B] rounded-xl p-4 hover:border-[#2D3B4F] transition-colors"
              data-testid={`equipment-card-${item.equipment_id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                    <Dumbbell className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">{item.name}</h4>
                    <p className="text-2xl font-bold text-primary">{item.weight} lbs</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => startEdit(item)}
                    className="p-2 hover:bg-[#2D3B4F] rounded-lg text-gray-400 hover:text-white"
                    data-testid={`edit-equipment-${item.equipment_id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.equipment_id)}
                    className="p-2 hover:bg-red-500/20 rounded-lg text-gray-400 hover:text-red-400"
                    data-testid={`delete-equipment-${item.equipment_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {item.keywords && item.keywords.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#1E293B]">
                  <p className="text-xs text-gray-500 mb-1">Keywords:</p>
                  <div className="flex flex-wrap gap-1">
                    {item.keywords.map((keyword, idx) => (
                      <span
                        key={idx}
                        className="text-xs bg-[#2D3B4F] text-gray-400 px-2 py-0.5 rounded"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowForm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#141B2D] border border-[#1E293B] rounded-2xl w-full max-w-md p-6"
              onClick={(e) => e.stopPropagation()}
              data-testid="equipment-form-modal"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">
                  {editingItem ? "Edit Equipment" : "Add Equipment"}
                </h2>
                <button onClick={() => setShowForm(false)} className="p-2 hover:bg-[#1E293B] rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Equipment Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Straight Bar, Safety Squat Bar"
                    className="w-full bg-[#1E293B] border border-[#1E293B] rounded-lg px-4 py-3 text-white focus:border-primary outline-none"
                    required
                    data-testid="equipment-name-input"
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Weight (lbs) *</label>
                  <input
                    type="number"
                    value={formData.weight}
                    onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                    placeholder="e.g., 45"
                    min="0"
                    step="0.5"
                    className="w-full bg-[#1E293B] border border-[#1E293B] rounded-lg px-4 py-3 text-white focus:border-primary outline-none"
                    required
                    data-testid="equipment-weight-input"
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-400 mb-1 block">
                    Keywords (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={formData.keywords}
                    onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                    placeholder="e.g., squat, bench, bar"
                    className="w-full bg-[#1E293B] border border-[#1E293B] rounded-lg px-4 py-3 text-white focus:border-primary outline-none"
                    data-testid="equipment-keywords-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    These words help match exercises to this equipment. If left empty, keywords are auto-generated from the name.
                  </p>
                </div>

                <div className="flex gap-4 pt-4 border-t border-[#1E293B]">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="flex-1 bg-[#1E293B] text-white py-3 rounded-lg font-semibold hover:bg-[#2D3B4F]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-primary text-white py-3 rounded-lg font-semibold hover:bg-blue-600 disabled:opacity-50"
                    data-testid="save-equipment-btn"
                  >
                    {saving ? "Saving..." : editingItem ? "Update" : "Add Equipment"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EquipmentManager;
